<template>
  <div v-if="products.length == 0 && !loading" class="no-results">
    <span>No results found</span>
  </div>
  <ListView
    class="product-list"
    :data-items="products"
    item="productTile"
    footer="productListFooter"
    @scroll="scrollHandler($event as unknown as Event)"
    data-cy="product-list"
  >
    <template v-slot:productTile="{ props }">
      <ProductTile :product="props.dataItem" />
    </template>
    <template v-slot:productListFooter>
      <Loader
        v-show="loading"
        class="products-loader"
        size="large"
        type="pulsing"
        data-cy="products-loader"
      />
    </template>
  </ListView>
</template>

<script setup lang="ts">
import type { OuKashUnionUkdProducts } from "@/Domain/OuKashUnionUkdProducts";
import ProductTile from "./ProductTile.vue";
import { ref, watchEffect } from "vue";
import { ListView } from "@progress/kendo-vue-listview";
import { Loader } from "@progress/kendo-vue-indicators";

const props = defineProps<{
  products: OuKashUnionUkdProducts[];
  totalProducts: number;
  defaultTake?: number;
  skip: number;
  loading: boolean;
}>();

const emit = defineEmits<{
  (e: "productsChanged", skip: number, take: number): void;
}>();

const take = ref<number>(props.defaultTake ?? 10);
const skip = ref<number>(props.skip);

const loadScrollThreshold = 100;

const scrollHandler = function (e: Event) {
  const target = e.target as HTMLDivElement | null;
  if (!target) return;
  if (
    target.scrollTop + loadScrollThreshold <
    target.scrollHeight - target.clientHeight
  )
    return;
  if (skip.value + take.value >= props.totalProducts) return;
  if (props.loading) return;

  skip.value = skip.value + take.value;
  emit("productsChanged", skip.value, take.value);
};

watchEffect(() => {
  skip.value = props.skip;
});
</script>

<style lang="scss">
.product-list {
  background: none;
  border: none;
  max-height: 70vh;
  margin-top: 16px;

  .k-listview-content {
    padding: 15px 15px;
  }

  .k-listview-footer {
    border-top-width: 0;
  }
}
.products-loader {
  display: flex;
  justify-content: center;
  margin: 32px 0;
}

.no-results {
  display: flex;
  justify-content: center;
  margin: 32px 0;
  font-size: 1.5rem;
  font-weight: 500;
  color: #6c757d;
}
</style>
